import { useEffect } from 'react'
import type { Control, FieldErrors, UseFormRegister } from 'react-hook-form'
import { useFieldArray } from 'react-hook-form'
import clsx from 'clsx'
import { sanitizeFieldName } from '@/utils/sanitizedLabel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons'

type Props = {
	id: string
	label: string
	required: boolean
	formStyle: string
	colSpan?: string
	register: UseFormRegister<any>
	control: Control<any>
	errors: FieldErrors
	className?: string
}

export default function MultipleTextInput({
	id,
	label,
	required,
	formStyle,
	colSpan = '1',
	register,
	control,
	errors,
	className,
}: Props) {
	const fieldName = sanitizeFieldName(label)

	const { fields, append, remove, insert } = useFieldArray({
		control,
		name: fieldName,
		shouldUnregister: true,
	})

	useEffect(() => {
		if (fields.length === 0) {
			append({ value: '' }, { shouldFocus: false })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const addInput = (index: number) => {
		insert(index + 1, { value: '' })
	}

	const removeInput = (index: number) => {
		if (fields.length > 1) {
			remove(index)
		}
	}

	// Helper function to get the error message
	const getErrorMessage = () => {
		if (!errors[fieldName]) return null

		// If it's an array error
		if (Array.isArray(errors[fieldName])) {
			const arrayErrors = errors[fieldName] as any[]
			const firstError = arrayErrors.find((error) => error?.value?.message)
			return firstError?.value?.message
		}

		// If it's a root error
		return errors[fieldName]?.message
	}

	return (
		<div
			className={clsx(
				'form-component',
				{
					'2': 'colSpan2',
				}[colSpan],
				className
			)}
		>
			{fields.map((field, index) => (
				<div key={field.id} className="flex items-center gap-2 mb-2">
					<div className="relative w-full">
						<div
							className={clsx(
								'form-element-container flex-1',
								formStyle === 'line' && 'line-style'
							)}
						>
							<input
								id={`${id}-${index}`}
								type="text"
								className="input w-full"
								placeholder=""
								{...register(`${fieldName}.${index}.value`, {
									required: required && index === 0 && `${label} is required`,
								})}
							/>
						</div>
						<label
							className="mb-2"
							htmlFor={`${id}-${index}`}
						>{`${label} ${required ? '*' : ''}`}</label>
					</div>

					<div className="flex gap-2">
						<button
							type="button"
							onClick={() => addInput(index)}
							className="w-10 h-10 flex items-center justify-center rounded-full bg-white-frosted hover:bg-white-frosted-shade"
							aria-label="Add input"
						>
							<FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
						</button>

						{fields.length > 1 && (
							<button
								type="button"
								onClick={() => removeInput(index)}
								className="w-10 h-10 flex items-center justify-center rounded-full bg-white-frosted hover:bg-white-frosted-shade"
								aria-label="Remove input"
							>
								<FontAwesomeIcon icon={faMinus} className="w-4 h-4" />
							</button>
						)}
					</div>
				</div>
			))}

			{/* Error message display */}
			{getErrorMessage() && <span className="mt-1 -500">{getErrorMessage()}</span>}
		</div>
	)
}
